import type { AiVisibilityReportId, AiVisibilityRunId } from '@brightlocal/shared';

import type {
  AiVisibilityCreateReportSettings,
  AiVisibilityGetReportsParams,
  AiVisibilityGetRunsParams,
  AiVisibilityUpdateReportSettings
} from './schema';
import {
  AiVisibilityCreateReportResponseSchema,
  AiVisibilityReportSchema,
  AiVisibilityReportsResponseSchema,
  AiVisibilityRunComparisonSchema,
  AiVisibilityRunReportResponseSchema,
  AiVisibilityRunSchema,
  AiVisibilityRunsResponseSchema,
  AiVisibilityUpdateReportResponseSchema
} from './schema';
import { HTTP_METHOD } from '../../../consts';
import { serializeRequestBody } from '../../../serializer';
import type { ApiCallOptions, Transport } from '../../../types';

export const createAiVisibilityApi = (transport: Transport) => ({
  getReports: (params?: AiVisibilityGetReportsParams, options?: ApiCallOptions) =>
    transport(`/manage/v1/ai-visibility/reports`, {
      method: HTTP_METHOD.GET,
      deserializer: AiVisibilityReportsResponseSchema,
      signal: options?.signal,
      // transport skips undefined/null keys, so unset params are omitted from the URL automatically
      queryParams: params
        ? { location_id: params.locationId, page: params.page, num_per_page: params.numPerPage }
        : undefined
    }),
  getReport: (reportId: AiVisibilityReportId, options?: ApiCallOptions) =>
    transport(`/manage/v1/ai-visibility/reports/${reportId}`, {
      method: HTTP_METHOD.GET,
      deserializer: AiVisibilityReportSchema,
      signal: options?.signal
    }),
  createReport: (settings: AiVisibilityCreateReportSettings, options?: ApiCallOptions) =>
    transport(`/manage/v1/ai-visibility/reports`, {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(settings),
      deserializer: AiVisibilityCreateReportResponseSchema,
      signal: options?.signal
    }),
  updateReport: (
    reportId: AiVisibilityReportId,
    settings: AiVisibilityUpdateReportSettings,
    options?: ApiCallOptions
  ) =>
    transport(`/manage/v1/ai-visibility/reports/${reportId}`, {
      method: HTTP_METHOD.PATCH,
      body: serializeRequestBody(settings),
      deserializer: AiVisibilityUpdateReportResponseSchema,
      signal: options?.signal
    }),
  getRuns: (reportId: AiVisibilityReportId, params?: AiVisibilityGetRunsParams, options?: ApiCallOptions) =>
    transport(`/manage/v1/ai-visibility/reports/${reportId}/runs`, {
      method: HTTP_METHOD.GET,
      deserializer: AiVisibilityRunsResponseSchema,
      signal: options?.signal,
      // transport skips undefined/null keys, so unset params are omitted from the URL automatically
      queryParams: params ? { page: params.page, num_per_page: params.numPerPage } : undefined
    }),
  getRun: (reportId: AiVisibilityReportId, runId: AiVisibilityRunId, options?: ApiCallOptions) =>
    transport(`/manage/v1/ai-visibility/reports/${reportId}/runs/${runId}`, {
      method: HTTP_METHOD.GET,
      deserializer: AiVisibilityRunSchema,
      signal: options?.signal
    }),
  getRunComparison: (reportId: AiVisibilityReportId, runId: AiVisibilityRunId, options?: ApiCallOptions) =>
    transport(`/manage/v1/ai-visibility/reports/${reportId}/runs/${runId}/comparison`, {
      method: HTTP_METHOD.GET,
      deserializer: AiVisibilityRunComparisonSchema,
      signal: options?.signal
    }),
  runReport: (reportId: AiVisibilityReportId, options?: ApiCallOptions) =>
    transport(`/manage/v1/ai-visibility/reports/${reportId}/actions/run`, {
      method: HTTP_METHOD.POST,
      deserializer: AiVisibilityRunReportResponseSchema,
      signal: options?.signal
    })
});

export type AiVisibilityApi = ReturnType<typeof createAiVisibilityApi>;
